import "./App.css";
import { useLocation, Navigate, Route, Routes } from "react-router-dom";
import Signup from "./pages/Signup/Signup";
import { AppSidebar } from "./common/AppSidebar/AppSidebar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { UserListDashboard } from "./pages/Dashboard/UserListDashboard/UserListDashboard";
import { HomeDashboard } from "./pages/Dashboard/HomeDashboard/HomeDashboard";
import UserInfoDashboard from "./pages/Dashboard/UserInfoDashboard/UserInfoDashboard";
import LoginPage from "./pages/LoginPage/LoginPage";
import PrivateRoute from "./utils/PrivateRoute/PrivateRoute";
import { dietRouteElements } from "./features/diet/routes";
import { linksRouteElements } from "./features/links/routes";
import { leadsRouteElements } from "./features/leads/routes";
import DietPlanDetailPage from "./features/diet/workflow/plan/view/DietPlanDetailPage";
import WorkoutDurationDetailPage from "./features/workouts/workflow/profile/view/WorkoutDurationDetailPage";
import { workoutRouteElements } from "./features/workouts/routes";
import { EmpDashboard } from "./pages/Dashboard/EmpDashboard/EmpDashboard";
import HrReferenceDataPage from "./features/hr/workflow/utils/ReferenceDataPage";
import { PlanDashboard } from "./pages/Dashboard/PlanDashboard/PlanDashboard";
import { ExecutiveAnalyticsDashboard } from "./pages/Dashboard/ExecutiveAnalyticsDashboard/ExecutiveAnalyticsDashboard";
import PlanCountryImages from "./pages/PlanCountryImages/PlanCountryImages";
import ForgotPasswordComponent from "./components/ForgotPasswordComponent/ForgotPasswordComponent";
import OtpComponent from "./components/LoginComponents/OtpComponent";
import UpdatePasswordComponent from "./components/UpdatePasswordComponent/UpdatePasswordComponent";
import { OversightRoute } from "./utils/OversightRoute";
import MessagesWorkspace from "./pages/comms/Messages/MessagesWorkspace";
import SupportWorkspace from "./pages/comms/Support/SupportWorkspace";
import CoachAnalytics from "./pages/comms/Coaches/CoachAnalytics";
import CoachDetail from "./pages/comms/Coaches/CoachDetail";
import OversightRoom from "./pages/comms/Oversight/OversightRoom";
import BookingsRoute from "./pages/comms/Bookings/BookingsRoute";
import BroadcastList from "./pages/comms/Broadcasts/BroadcastList";
import BroadcastComposer from "./pages/comms/Broadcasts/BroadcastComposer";
import BroadcastAnalytics from "./pages/comms/Broadcasts/BroadcastAnalytics";
import { ChatDockProvider } from "./features/chatDock/ChatDockProvider";
import { ChatDock } from "./features/chatDock/ChatDock";
import { useSidebarCollapsed } from "./hooks/useSidebarCollapsed";

const AUTH_ROUTES_HIDE_SIDEBAR = new Set([
  "/",
  "/login",
  "/signup",
  "/forgot-password",
  "/otp",
  "/update-password",
]);

/** Expanded rail width (Tailwind w-56 = 14rem). */
const SIDEBAR_WIDTH_EXPANDED = "14rem";
/** Collapsed icon rail (Tailwind w-16 = 4rem). */
const SIDEBAR_WIDTH_COLLAPSED = "4rem";

function App() {
  const location = useLocation();
  const hideSidebar = AUTH_ROUTES_HIDE_SIDEBAR.has(location.pathname);
  const { collapsed: sidebarCollapsed, toggleCollapsed: toggleSidebarCollapsed } =
    useSidebarCollapsed();

  const appRoutes = (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LoginPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/otp" element={<OtpComponent />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPasswordComponent />} />
      <Route path="/update-password" element={<UpdatePasswordComponent />} />

      {/* Protected routes */}
      <Route path="/user" element={<PrivateRoute element={UserListDashboard} />} />
      <Route path="/hr/employees" element={<PrivateRoute element={EmpDashboard} />} />
      <Route path="/hr/reference-data" element={<PrivateRoute element={HrReferenceDataPage} />} />
      <Route path="/home" element={<PrivateRoute element={HomeDashboard} />} />
      <Route
        path="/dashboard/:id/diet-plan/:planId"
        element={<PrivateRoute element={DietPlanDetailPage} />}
      />
      <Route
        path="/dashboard/:id/workout-plan/:durationId"
        element={<PrivateRoute element={WorkoutDurationDetailPage} />}
      />
      <Route path="/dashboard/:id" element={<PrivateRoute element={UserInfoDashboard} />} />
      <Route path="/plan" element={<PrivateRoute element={PlanDashboard} />} />
      <Route
        path="/revenue"
        element={<PrivateRoute element={ExecutiveAnalyticsDashboard} />}
      />
      <Route path="/plan-images" element={<PrivateRoute element={PlanCountryImages} />} />

      {/* Flex Comms */}
      <Route path="/messages" element={<PrivateRoute element={MessagesWorkspace} />} />
      <Route path="/messages/:roomId" element={<PrivateRoute element={MessagesWorkspace} />} />
      <Route path="/bookings" element={<PrivateRoute element={BookingsRoute} />} />
      <Route path="/broadcasts" element={<PrivateRoute element={BroadcastList} />} />
      <Route path="/broadcasts/new" element={<PrivateRoute element={BroadcastComposer} />} />
      {/* No edit route: v1_10_0 has no PATCH on a broadcast, so the composer
          is create-only. A stale /broadcasts/:id/edit link lands on the list
          rather than a blank screen. */}
      <Route path="/broadcasts/:id/edit" element={<Navigate to="/broadcasts" replace />} />
      <Route
        path="/broadcasts/:id/analytics"
        element={<PrivateRoute element={BroadcastAnalytics} />}
      />
      {/* One workspace for agents and leads — `:id` only selects a row, so the
          queue never unmounts and triage doesn't lose its place. */}
      <Route path="/support" element={<PrivateRoute element={SupportWorkspace} />} />
      <Route path="/support/console" element={<PrivateRoute element={SupportWorkspace} />} />
      <Route
        path="/support/tickets/:id"
        element={<PrivateRoute element={SupportWorkspace} />}
      />
      <Route path="/coaches" element={<OversightRoute element={CoachAnalytics} />} />
      <Route path="/coaches/:coachId" element={<OversightRoute element={CoachDetail} />} />
      <Route path="/oversight/:roomId" element={<OversightRoute element={OversightRoom} />} />

      {dietRouteElements}
      {linksRouteElements}
      {leadsRouteElements}
      {workoutRouteElements}
    </Routes>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {hideSidebar ? (
        <div className="h-screen w-screen overflow-auto">{appRoutes}</div>
      ) : (
        // The chat dock lives inside this branch only, so it never appears on
        // login/signup/OTP, and as a SIBLING of <main> rather than a child —
        // main's `overflow-y-auto` would otherwise clip the floating bubble.
        <ChatDockProvider>
          <div className="flex h-screen w-screen overflow-hidden">
            {/*
              Sidebar participates in document flow (not fixed+margin).
              Main is flex-1 min-w-0 so the viewport is literally screen − sidebar.
            */}
            <aside
              className="h-screen flex-shrink-0 z-30 transition-[width] duration-300 ease-in-out overflow-hidden"
              style={{
                width: sidebarCollapsed
                  ? SIDEBAR_WIDTH_COLLAPSED
                  : SIDEBAR_WIDTH_EXPANDED,
              }}
              aria-label="Primary navigation"
            >
              <AppSidebar
                collapsed={sidebarCollapsed}
                onToggleCollapse={toggleSidebarCollapsed}
              />
            </aside>
            <main className="flex-1 min-w-0 h-screen overflow-y-auto overflow-x-hidden">
              {appRoutes}
            </main>
          </div>
          <ChatDock />
        </ChatDockProvider>
      )}
    </LocalizationProvider>
  );
}

export default App;
